import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {CURRENCY_CODES} from '../../elements/currencyCodes';
import {ArrowRight, ChartBar, PencilSimple, Warning} from '../../icons';
import {evidenceKindOf, evidenceNoneIsAboutTheAccount, evidenceNoneKeyOf, isBudgetResolved,
        suggestedMonthlyFor} from '../budgetMath';

/**
 * El presupuesto de UNA cuenta: de dónde sale el número, la flecha y el monto que se puede editar.
 *
 * Es la presentación del paso 4 del túnel sacada de su reducer: recibe la sugerencia del backend y
 * la edición del usuario, y avisa con `onEdit(amount, currency)`. Así la usan el paso 4 (a través de
 * `BudgetRow`, que la conecta a `EDIT_BUDGET`) y el modal «Desde tus cuentas conectadas» del Panel,
 * que guarda las ediciones en su propio estado. Una sola pieza: si la evidencia o el editor cambian,
 * cambian en los dos lugares.
 *
 * Dibuja TRES celdas hermanas (evidencia, flecha, monto) y no un contenedor: cada lugar las acomoda
 * en su propia grilla.
 *
 * `status` es para quien pide los montos fila por fila y los muestra mientras llegan: `'loading'`
 * mientras la sugerencia viaja y `'failed'` si no llegó. Sin eso, una fila sin respuesta todavía
 * diría «sin gasto el último mes», que es un dato falso sobre la cuenta.
 *
 * `resolved` es para quien ya resolvió el presupuesto que VIAJA en el POST —hoy, sólo el modal del
 * Panel—: `{budget: {amount, currency} | null, note}`. Con eso la celda del monto pinta ese
 * presupuesto y no la sugerencia cruda. Hacía falta porque la moneda se resolvía en dos lugares: la
 * fila mostraba «1.300.000» sin moneda o «CAD 13.000», y el POST mandaba la moneda de respaldo, o
 * nada porque el backend no admite CAD (review C-modal-lineas-01/02). `note` dice por qué una cuenta
 * que tenía un monto nace sin él. Sin `resolved` (el paso 4 del túnel), nada cambia.
 *
 * `offerNoBudget` suma al editor el botón «Sin presupuesto», que avisa `onEdit(null, moneda)`. Lo usa
 * el paso 4 (H-03): Pablo quería arrancar un cliente sin presupuesto, no encontró cómo y apretó el
 * «Quitar» de la fila, que sacaba al cliente entero. Antes sólo se lograba dejando el monto vacío o
 * en 0. Es opcional para que el modal del Panel no cambie.
 */
export function BudgetAmount({suggestion, edit, onEdit, preparing = true, catalogRequested = true,
                              canEditCurrency = false, status = null, resolved = undefined,
                              offerNoBudget = false}) {
    const {t, i18n} = useTranslation();
    // El monto y la moneda se editan JUNTOS y se guardan juntos: son un borrador, no dos controles
    // sueltos. Con `EDIT_BUDGET` por cada uno, cambiar la moneda antes de tocar el número guardaba
    // un presupuesto a medio escribir.
    const [draft, setDraft] = useState(null);
    const rawAmount = amountFor(suggestion, edit);
    const amount = resolved === undefined ? rawAmount : (resolved.budget ? resolved.budget.amount : null);
    const currency = resolved?.budget ? resolved.budget.currency : currencyFor(suggestion, edit);
    // Con un motivo, editar sólo sirve si se puede elegir otra moneda: sin el selector, el monto que
    // se escriba vuelve a nacer en la misma moneda que el backend no admite (o en ninguna).
    const canDefine = !resolved?.note || canEditCurrency;
    const evidence = evidenceKindOf(suggestion);
    // El ámbar y el triángulo son para lo que pasa en la CUENTA (dormida, sin presupuesto legible,
    // canal sin presupuestos). Con `UNKNOWN` el que no llegó es el catálogo: se dice, en tono neutro.
    // `preparing` viaja desde el paso porque el mismo `reason` significa dos cosas distintas según
    // el poll siga vivo o no: con el poll detenido, «todavía estamos leyendo» pasa a ser mentira.
    const noneIsAboutTheAccount = evidenceNoneIsAboutTheAccount(suggestion, preparing, catalogRequested);
    const noneKey = evidenceNoneKeyOf(suggestion, preparing, catalogRequested);

    // `canEditCurrency` se congela al abrir: llega de un GET que puede contestar DESPUÉS, y un
    // `<select>` que aparece solo en el medio de la edición mueve el campo que el usuario está
    // tipeando.
    // Con un motivo, el editor arranca con el número que la cuenta traía: se viene a cambiar la
    // moneda, no a volver a escribirlo.
    const seed = amount === null || amount === undefined ? (resolved?.note ? rawAmount : null) : amount;
    const startEditing = () => setDraft({amount: seed === null || seed === undefined ? '' : String(seed),
                                         currency: currency || '',
                                         canEditCurrency});

    const commit = () => {
        if (!draft) return;
        // La moneda vuelve a ser la de la cuenta si el selector no se ofreció: el reducer guarda el
        // par completo, y mandar una moneda vacía dejaría el ítem afuera del payload.
        onEdit(Number(draft.amount), draft.currency || currency);
        setDraft(null);
    };

    // `null` y no 0: con 0 el total de un cliente con varias cuentas pasaba a decir «ARS 0», y
    // `null` es «sin monto» en toda la pantalla (ver `totalsByCurrencyOf`). El payload lo trata igual
    // que a un 0: la línea nace sin presupuesto (`budgetForAccount`).
    const clearBudget = () => {
        onEdit(null, (draft && draft.currency) || currency);
        setDraft(null);
    };

    if (status === 'loading') {
        return (
            <>
                <span className="ob-budget__evidence" role="status">
                    <span className="ob-spinner-mini"/>
                    {t('onboarding.budgets.evidence_loading')}
                </span>
                <ArrowRight size={14} className="ob-budget__arrow"/>
                {/* Vacío y no «—»: un guion se lee como «esta cuenta no tiene monto», y todavía no
                    lo sabemos. La celda se dibuja igual para que la grilla no se corra. */}
                <div className="ob-budget__amount"/>
            </>
        );
    }

    return (
        <>
            {status === 'failed' ? (
                /* Un error nuestro no se pinta como un hecho de la cuenta: «sin gasto el último mes»
                   sobre una respuesta que no llegó afirma algo que nadie leyó. */
                <span className="ob-budget__evidence -zero">
                    <Warning size={13}/>
                    {t('onboarding.budgets.evidence_failed')}
                </span>
            ) : (
                <span className={`ob-budget__evidence${evidence === 'none' && noneIsAboutTheAccount ? ' -zero' : ''}`}>
                    {evidence === 'observed' && (
                        <>
                            <ChartBar size={13}/>
                            {/* Con `suggestion.currency` y NO con la moneda editada: esto es el gasto
                                que la plataforma observó el mes pasado, un hecho pasado con su propia
                                unidad. Al tomar la moneda del editor, cambiar el presupuesto de ARS a
                                USD reescribía el rótulo del gasto sin tocar el número —la fila pasaba
                                a afirmar que gastó 186.799 dólares cuando gastó 186.799 pesos, mil
                                veces más—, y encima es justo el dato que sostiene la confianza en el
                                monto sugerido. */}
                            {t('onboarding.budgets.evidence_observed', {amount: formatMoney(suggestion.amount, suggestion.currency, i18n.language),
                                                                        interpolation: {escapeValue: false}})}
                        </>
                    )}
                    {evidence === 'estimated' && (
                        <>
                            <ChartBar size={13}/>
                            {t('onboarding.budgets.evidence_estimated')}
                        </>
                    )}
                    {evidence === 'none' && (
                        <>
                            {noneIsAboutTheAccount && <Warning size={13}/>}
                            {/* El texto sale del `reason` del payload. El fallback es la clave de
                                siempre: un valor que no reconocemos no puede dejar la fila muda. */}
                            {noneKey ? t(noneKey, t('onboarding.budgets.evidence_none'))
                                     : t('onboarding.budgets.evidence_none')}
                        </>
                    )}
                </span>
            )}

            <ArrowRight size={14} className="ob-budget__arrow"/>

            <div className="ob-budget__amount">
                {draft ? (
                    /* El guardado es al salir del editor ENTERO, no del input: con el selector de
                       moneda al lado, «salir del input» es exactamente lo que pasa al ir a tocarlo,
                       y cerrar ahí dejaba la moneda sin cambiar. `relatedTarget` distingue irse a
                       otro control de adentro de irse afuera. */
                    <div className="ob-budget__editor"
                         onBlur={event => {
                             if (event.currentTarget.contains(event.relatedTarget)) return;
                             commit();
                         }}>
                        {/* Moneda y campo, juntos: con «Sin presupuesto» en el editor, a 390 la moneda
                            quedaba sola en un renglón y el campo en otro. */}
                        <span className="ob-budget__editor-fields">
                            {draft.canEditCurrency ? (
                                <select className="ob-budget__currency" value={draft.currency}
                                        onChange={event => setDraft({...draft, currency: event.target.value})}
                                        aria-label={t('onboarding.budgets.edit_currency')}>
                                    {currencyOptionsFor(draft.currency).map(code => (
                                        <option value={code} key={code}>{code || '—'}</option>
                                    ))}
                                </select>
                            ) : currency && (
                                /* Con el módulo apagado la moneda se muestra y no se toca. El `title`
                                   es lo único que distingue «tu plan no lo incluye» de «no pudimos
                                   preguntar»: sin él, las dos se ven igual —un texto gris— y la agencia
                                   que SÍ tiene multi-moneda no tiene forma de saber por qué no puede
                                   elegirla. */
                                <span className="ob-budget__fixed-currency"
                                      title={t('onboarding.budgets.currency_locked')}>{currency}</span>
                            )}
                            <input autoFocus type="number" className="ob-budget__input" value={draft.amount}
                                   onChange={event => setDraft({...draft, amount: event.target.value})}
                                   onKeyDown={event => {
                                       if (event.key === 'Enter') commit();
                                       if (event.key === 'Escape') setDraft(null);
                                   }}
                                   aria-label={t('onboarding.budgets.edit')}/>
                        </span>
                        {/* El `preventDefault` del mousedown deja el foco en el campo: en Safari un
                            botón no toma el foco al clic, el `blur` llegaba sin `relatedTarget`,
                            guardaba lo tipeado y desmontaba el editor antes del clic. Con el
                            teclado, el Tab sí lo enfoca y el `blur` lo reconoce como de adentro. */}
                        {offerNoBudget && (
                            <button type="button" className="ob-link -muted ob-budget__clear"
                                    onMouseDown={event => event.preventDefault()} onClick={clearBudget}>
                                {t('onboarding.budgets.set_no_budget')}
                            </button>
                        )}
                    </div>
                ) : amount ? (
                    /* El lápiz y la palabra, además del subrayado punteado que ya estaba: en la
                       pasada del 07/09 el número se leyó como texto —«no me daba cuenta ni en
                       pedo»—, y un affordance que hay que descubrir pasando el mouse no existe
                       para quien no lo pasa. */
                    <button type="button" className="ob-budget__value" onClick={startEditing}>
                        {formatMoney(amount, currency, i18n.language)}
                        <span className="ob-budget__per">{t('onboarding.budgets.per_month')}</span>
                        <span className="ob-budget__pencil">
                            <PencilSimple size={12}/> {t('onboarding.budgets.edit_short')}
                        </span>
                    </button>
                ) : (
                    <>
                        {/* Mientras la cuenta espera el catálogo, «Arranca sin presupuesto» afirma
                            algo que todavía no sabemos —y que en un momento pasa a ser un monto— (card
                            C9zl8x1e). Sólo donde el catálogo se pidió y el poll sigue vivo: en el modal
                            del Panel no se pide, y con `status` la fila ya dice lo suyo. Editar sigue
                            disponible. */}
                        {status === null && preparing && catalogRequested && !edit && !isBudgetResolved(suggestion)
                            ? <Calculating/>
                            : <span className="ob-budget__empty">{t('onboarding.budgets.no_budget')}</span>}
                        {canDefine && (
                            <button type="button" className="ob-btn -mini -ghost" onClick={startEditing}>
                                {t('onboarding.budgets.define_now')}
                            </button>
                        )}
                        {resolved?.note && <span className="ob-budget__note">{resolved.note}</span>}
                    </>
                )}
            </div>
        </>
    );
}

/**
 * «Calculando…»: un monto que todavía va a llegar, dicho como tal y no como un número. Lo usan la
 * fila de una cuenta (arriba) y el total de un cliente con varias (`BudgetRow`).
 */
export function Calculating() {
    const {t} = useTranslation();
    return (
        <span className="ob-budget__calculating" role="status">
            <span className="ob-spinner-mini" aria-hidden="true"/>
            {t('onboarding.budgets.calculating')}
        </span>
    );
}

/** El monto que la fila muestra: lo que editó el usuario o, si no tocó nada, la sugerencia. */
export function amountFor(suggestion, edit) {
    if (edit) return edit.amount;
    return suggestedMonthlyFor(suggestion);
}

export function currencyFor(suggestion, edit) {
    return edit?.currency || suggestion?.currency || null;
}

/**
 * El monto, en la moneda de la cuenta.
 *
 * Sin moneda no se inventa una: el backend la devuelve nula cuando la cuenta no la tiene declarada,
 * y poner la de la agencia haría ver un número en pesos que en realidad son dólares.
 */
export function formatMoney(amount, currency, locale) {
    if (amount === null || amount === undefined) return '—';
    // Con el idioma del usuario y no uno fijo: en inglés, los miles separados a la manera argentina
    // se leen como decimales — «ARS 3.000.000» pasa a entenderse como tres.
    //
    // `useGrouping: 'always'`: en `es` la norma CLDR no separa los números de CUATRO cifras
    // (`minimumGroupingDigits: 2`), así que la misma pantalla mostraba «USD 6200» al lado de
    // «ARS 38.063» (card C9zl8x1e). Un navegador que no conozca el valor lo toma como `true`, que es
    // el comportamiento de antes.
    const formatted = new Intl.NumberFormat(locale || 'es-AR', {maximumFractionDigits: 0, useGrouping: 'always'})
        .format(amount);
    return currency ? `${currency} ${formatted}` : formatted;
}

/**
 * Las monedas que se pueden elegir, sin inventar una lista nueva.
 *
 * Sale de la misma lista que usa el alta de una línea a mano, que a su vez espeja
 * `currency/utils.py` — lo que el backend valida al crear la línea. La moneda que ya tiene la
 * cuenta se agrega si no está: dejarla afuera se la cambiaría sola con sólo abrir el editor.
 */
function currencyOptionsFor(current) {
    const codes = CURRENCY_CODES;
    if (!current) return [''].concat(codes);
    return codes.includes(current) ? codes : [current].concat(codes);
}
